import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exportFramesCsv } from '../lib/canlog/exportCsv';
import { exportFramesJson } from '../lib/canlog/exportJson';
import type { CanFrame } from '../lib/canlog/types';
import { DecodeContributionModal, type ContributionIntent } from '../components/DecodeContributionModal';
import { SignalGraphPanel } from '../components/decode/SignalGraphPanel';
import {
  explainDeviceError,
  fetchLiveFramesDetailed,
  fetchDeviceStatus,
  getDeviceFrameEndpointUrls,
  getDeviceLiveUrl,
  getDeviceLocalUiUrl,
  getDeviceStatusUrl,
  isLikelyMixedContentBlocked,
  normalizeDeviceBaseUrl,
  type DeviceStatus
} from '../lib/live/deviceClient';
import { buildLiveStats } from '../lib/live/liveStats';
import { makeDemoFrames, type NormalizedLiveFrame } from '../lib/live/normalizeLiveFrames';
import {
  fetchSignalHintsForCanIds,
  groupedVisibleSignalHints,
  groupSignalHintsByCanId,
  signalHintBadgeLabel,
  signalHintSourceLabel,
  vehicleContextLabel,
  type SignalHint
} from '../lib/decode/signalHints';
import { getMostFrequentCanId } from '../lib/decode/graphSeries';
import { track } from '../lib/analytics';

const RENDER_LIMIT = 1000;
const CAPTURE_LIMIT = 50000;
const POLL_MS = 1000;
const DECODE_BATCH_SIZE = 500;
const DECODE_INTERVAL_MS = 2500;

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'unreachable';
type LiveState = 'stopped' | 'live' | 'paused';
type SourceMode = 'device' | 'demo';
type FrameStreamState = 'idle' | 'checking' | 'receiving' | 'empty' | 'blocked' | 'unavailable' | 'error';

type DecodeProfile = {
  id: string;
  displayName: string;
  confidenceSummary: {
    label: string;
    value: number;
  };
};

type DecodedSignal = {
  signalName: string;
  value: number | string | boolean;
  unit?: string;
  badge: string;
  confidence: {
    label: string;
    value: number;
  };
};

type DecodeFrameResult = {
  canIdHex: string;
  timestampMs?: number;
  messageName?: string;
  decodedSignals: DecodedSignal[];
};

type DecodedRow = {
  key: string;
  timestampMs?: number;
  canIdHex: string;
  messageName?: string;
  signal: DecodedSignal;
};

type StatusSeverity = 'healthy' | 'warning' | 'critical' | 'unknown';

type DeviceStatusCard = {
  label: string;
  value: unknown;
  kind?: 'boolean' | 'bytes' | 'millivolts' | 'integer' | 'text';
  warningWhenPositive?: boolean;
};

function formatDuration(ms: number) {
  if (ms <= 0) return '0.0 s';
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatTimestamp(ms: number | null | undefined) {
  if (typeof ms !== 'number') return 'n/a';
  return `${ms.toFixed(3)} ms`;
}

function asNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asBoolean(value: unknown) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'yes', '1', 'on', 'active'].includes(normalized)) return true;
    if (['false', 'no', '0', 'off', 'inactive'].includes(normalized)) return false;
  }
  return null;
}

function formatBytes(value: unknown) {
  const bytes = asNumber(value);
  if (bytes === null) return 'Unavailable';
  if (bytes < 1024) return `${bytes.toLocaleString()} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function formatDeviceValue(value: unknown, kind: DeviceStatusCard['kind'] = 'text') {
  if (value === undefined || value === null || value === '') return 'Unavailable';
  if (kind === 'bytes') return formatBytes(value);
  if (kind === 'millivolts') {
    const millivolts = asNumber(value);
    return millivolts === null ? 'Unavailable' : `${(millivolts / 1000).toFixed(3)} V`;
  }
  if (kind === 'integer') {
    const number = asNumber(value);
    return number === null ? String(value) : Math.trunc(number).toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function readStatus(status: DeviceStatus | null, keys: string[]) {
  if (!status) return undefined;
  for (const key of keys) {
    if (status[key] !== undefined) return status[key];
  }
  return undefined;
}

function getStorageUsed(status: DeviceStatus | null) {
  return readStatus(status, ['storage_used', 'storage_used_bytes', 'sd_used', 'used_bytes']);
}

function getStorageFree(status: DeviceStatus | null) {
  return readStatus(status, ['storage_free', 'storage_free_bytes', 'sd_free', 'free_bytes']);
}

function getRxOverflow(status: DeviceStatus | null) {
  return readStatus(status, ['can_rx_overflow', 'can_hardware_rx_overflow', 'rx_overflow', 'rx_overflows']);
}

function getDeviceHealth(status: DeviceStatus | null): { severity: StatusSeverity; reasons: string[] } {
  if (!status) return { severity: 'unknown', reasons: [] };
  const controllerAlive = asBoolean(readStatus(status, ['can_controller_alive', 'controller_alive']));
  const busOff = asBoolean(readStatus(status, ['can_bus_off', 'bus_off']));
  const ringDrops = asNumber(readStatus(status, ['ring_drops', 'drops'])) ?? 0;
  const rxOverflow = asNumber(getRxOverflow(status)) ?? 0;
  const canErrorCount = asNumber(readStatus(status, ['can_error_count', 'error_count'])) ?? 0;
  const reasons: string[] = [];

  if (controllerAlive === false) reasons.push('CAN controller is not alive');
  if (busOff === true) reasons.push('CAN bus is off');
  if (rxOverflow > 0) reasons.push(`RX overflow: ${rxOverflow.toLocaleString()}`);
  if (canErrorCount >= 10) reasons.push(`CAN error count: ${canErrorCount.toLocaleString()}`);
  if (ringDrops > 0) reasons.push(`Ring drops: ${ringDrops.toLocaleString()}`);

  if (busOff === true || controllerAlive === false) return { severity: 'critical', reasons };
  if (rxOverflow > 0 || canErrorCount >= 10 || ringDrops > 0) return { severity: 'warning', reasons };
  if (controllerAlive === true && busOff === false && ringDrops === 0) return { severity: 'healthy', reasons };
  return { severity: 'unknown', reasons };
}

function severityCopy(severity: StatusSeverity) {
  if (severity === 'healthy') return 'Healthy';
  if (severity === 'warning') return 'Warning';
  if (severity === 'critical') return 'Critical';
  return 'Unknown';
}

function frameStreamCopy(state: FrameStreamState) {
  if (state === 'checking') return 'checking';
  if (state === 'receiving') return 'receiving frames';
  if (state === 'empty') return 'reachable, no frames';
  if (state === 'blocked') return 'blocked';
  if (state === 'unavailable') return 'unavailable';
  if (state === 'error') return 'error';
  return 'idle';
}

function renderStatusValue(card: DeviceStatusCard) {
  if (card.kind !== 'boolean') return <strong>{formatDeviceValue(card.value, card.kind)}</strong>;
  const value = asBoolean(card.value);
  if (value === null) return <strong>Unavailable</strong>;
  return <span className={`health-badge ${value ? 'good' : 'bad'}`}>{value ? 'Yes' : 'No'}</span>;
}

function statusCardClass(card: DeviceStatusCard) {
  const positiveWarning = card.warningWhenPositive && (asNumber(card.value) ?? 0) > 0;
  return `status-card${positiveWarning ? ' status-card-warning' : ''}`;
}

function getInvalidIpMessage(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const host = trimmed.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];
  if (!/^\d+\.\d+\.\d+\.\d+$/.test(host)) return '';
  const octets = host.split('.').map((part) => Number.parseInt(part, 10));
  return octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255) ? 'Invalid IP address. Use format 192.168.1.120.' : '';
}

function normalizeQuery(value: string) {
  return value.trim().toUpperCase().replace(/\s+/g, ' ');
}

function filterFrames(frames: NormalizedLiveFrame[], idFilter: string, dataFilter: string) {
  const idQuery = normalizeQuery(idFilter);
  const dataQuery = normalizeQuery(dataFilter);
  const dataCompact = dataQuery.replace(/[^0-9A-F]/g, '');

  return frames.filter((frame) => {
    const idMatches = !idQuery || frame.canIdHex.toUpperCase().includes(idQuery) || frame.canIdHex.replace(/^0X/, '').includes(idQuery.replace(/^0X/, ''));
    const dataMatches = !dataQuery || frame.dataHex.includes(dataQuery) || frame.dataHex.replace(/\s/g, '').includes(dataCompact);
    return idMatches && dataMatches;
  });
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function toDecodeFrames(frames: CanFrame[]) {
  return frames.map((frame) => ({
    timestampMs: frame.timestampMs,
    canId: frame.canId,
    extended: frame.extended,
    dlc: frame.dlc,
    data: frame.data
  }));
}

function safeEndpointPath(value: string) {
  if (!value) return undefined;
  try {
    return new URL(value).pathname;
  } catch {
    return value.replace(/^https?:\/\/[^/]+/i, '') || undefined;
  }
}

function connectionGuidance(state: ConnectionState, frameState: FrameStreamState, sourceMode: SourceMode) {
  if (sourceMode === 'demo') return 'Demo mode started. Simulated CAN frames are now available.';
  if (state === 'idle') return 'Connection idle. Choose a quick IP or enter your SmartCAN device IP.';
  if (state === 'connecting') return 'Checking the device status endpoint...';
  if (state === 'connected' && frameState === 'receiving') return 'Device connected and live frames are streaming.';
  if (state === 'connected') return 'Device connected. Start live capture when ready.';
  return 'Connection failed. Check the IP address, Wi-Fi network, CORS, and browser mixed-content limits.';
}

export function LivePage() {
  const [deviceInput, setDeviceInput] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [liveState, setLiveState] = useState<LiveState>('stopped');
  const [sourceMode, setSourceMode] = useState<SourceMode>('device');
  const [status, setStatus] = useState<DeviceStatus | null>(null);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [notice, setNotice] = useState('');
  const [frames, setFrames] = useState<NormalizedLiveFrame[]>([]);
  const [idFilter, setIdFilter] = useState('');
  const [dataFilter, setDataFilter] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [decodeProfiles, setDecodeProfiles] = useState<DecodeProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState('generic-obd-ii');
  const [decodeEnabled, setDecodeEnabled] = useState(false);
  const [decodeRows, setDecodeRows] = useState<DecodedRow[]>([]);
  const [decodeError, setDecodeError] = useState('');
  const [displayedTime, setDisplayedTime] = useState(new Date());
  const [contributionIntent, setContributionIntent] = useState<ContributionIntent | null>(null);
  const [signalHints, setSignalHints] = useState<SignalHint[]>([]);
  const [signalHintState, setSignalHintState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [signalHintError, setSignalHintError] = useState('');
  const [selectedGraphCanId, setSelectedGraphCanId] = useState<number | null>(null);
  const [frameStreamState, setFrameStreamState] = useState<FrameStreamState>('idle');
  const [lastStatusFetchTime, setLastStatusFetchTime] = useState<string | null>(null);
  const [lastStatusFetchError, setLastStatusFetchError] = useState('');
  const [lastFrameFetchTime, setLastFrameFetchTime] = useState<string | null>(null);
  const [lastFrameFetchError, setLastFrameFetchError] = useState('');
  const [activeFrameEndpointUrl, setActiveFrameEndpointUrl] = useState('');
  const [lastFrameHttpStatus, setLastFrameHttpStatus] = useState<number | null>(null);
  const [lastRawFrameCount, setLastRawFrameCount] = useState(0);
  const [lastParsedFrameCount, setLastParsedFrameCount] = useState(0);
  const [lastInvalidFrameCount, setLastInvalidFrameCount] = useState(0);

  const pollTimerRef = useRef<number | null>(null);
  const decodeTimerRef = useRef<number | null>(null);
  const framesRef = useRef<NormalizedLiveFrame[]>([]);
  const decodeCursorRef = useRef(0);
  const baseUrl = useMemo(() => normalizeDeviceBaseUrl(deviceInput), [deviceInput]);
  const attemptedStatusUrl = useMemo(() => getDeviceStatusUrl(baseUrl), [baseUrl]);
  const attemptedFrameUrl = useMemo(() => getDeviceLiveUrl(baseUrl), [baseUrl]);
  const attemptedFrameUrls = useMemo(() => getDeviceFrameEndpointUrls(baseUrl), [baseUrl]);
  const deviceLocalUiUrl = useMemo(() => getDeviceLocalUiUrl(baseUrl), [baseUrl]);
  const mixedContentLikely = useMemo(() => Boolean(baseUrl && isLikelyMixedContentBlocked(baseUrl)), [baseUrl]);
  const invalidIpMessage = useMemo(() => getInvalidIpMessage(deviceInput), [deviceInput]);
  const observedCanIds = useMemo(() => Array.from(new Set(frames.map((frame) => frame.canId))).sort((a, b) => a - b), [frames]);
  const observedCanIdsKey = useMemo(() => observedCanIds.join(','), [observedCanIds]);
  const signalHintsByCanId = useMemo(() => groupSignalHintsByCanId(signalHints), [signalHints]);
  const visibleSignalHintGroups = useMemo(() => groupedVisibleSignalHints(signalHints, 3), [signalHints]);
  const statusApiState = status
    ? 'reachable'
    : connectionState === 'connecting'
      ? 'checking'
      : lastStatusFetchError
        ? 'unreachable'
        : 'idle';

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const appendFrames = useCallback((nextFrames: NormalizedLiveFrame[]) => {
    if (nextFrames.length === 0) return;
    setFrames((current) => {
      const combined = [...current, ...nextFrames];
      if (combined.length > CAPTURE_LIMIT) {
        setWarning(`Capture buffer reached ${CAPTURE_LIMIT.toLocaleString()} frames. Oldest frames are being dropped.`);
        return combined.slice(-CAPTURE_LIMIT).map((frame, index) => ({ ...frame, index }));
      }
      return combined;
    });
  }, []);

  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  useEffect(() => {
    if (selectedGraphCanId !== null || frames.length === 0) return;
    setSelectedGraphCanId(getMostFrequentCanId(frames));
  }, [frames, selectedGraphCanId]);

  useEffect(() => {
    let active = true;
    fetch('/api/decode/profiles')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Decode profiles unavailable')))
      .then((payload: { profiles?: DecodeProfile[] }) => {
        if (!active) return;
        const profiles = Array.isArray(payload.profiles) ? payload.profiles : [];
        setDecodeProfiles(profiles);
        if (profiles.length > 0) setSelectedProfileId(profiles[0].id);
      })
      .catch(() => {
        if (active) setDecodeProfiles([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
      if (decodeTimerRef.current !== null) window.clearInterval(decodeTimerRef.current);
    };
  }, [stopPolling]);

  useEffect(() => {
    const timer = window.setInterval(() => setDisplayedTime(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    decodeCursorRef.current = 0;
    setDecodeRows([]);
    setDecodeError('');
  }, [selectedProfileId, decodeEnabled]);

  useEffect(() => {
    if (!observedCanIdsKey) {
      setSignalHints([]);
      setSignalHintState('idle');
      setSignalHintError('');
      return;
    }

    let active = true;
    const idsForLookup = observedCanIdsKey.split(',').map((value) => Number.parseInt(value, 10));
    const timer = window.setTimeout(() => {
      setSignalHintState('loading');
      setSignalHintError('');
      fetchSignalHintsForCanIds(idsForLookup)
        .then((hints) => {
          if (!active) return;
          setSignalHints(hints);
          setSignalHintState('success');
        })
        .catch(() => {
          if (!active) return;
          setSignalHintState('error');
          setSignalHintError('Signal hints unavailable.');
        });
    }, 650);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [observedCanIdsKey]);

  useEffect(() => {
    if (!decodeEnabled) {
      if (decodeTimerRef.current !== null) {
        window.clearInterval(decodeTimerRef.current);
        decodeTimerRef.current = null;
      }
      return;
    }

    async function decodeNextBatch() {
      const allFrames = framesRef.current;
      if (allFrames.length <= decodeCursorRef.current) return;

      const batchStart = decodeCursorRef.current;
      const batch = allFrames.slice(batchStart, batchStart + DECODE_BATCH_SIZE);
      decodeCursorRef.current = batchStart + batch.length;

      try {
        const response = await fetch('/api/decode/frames', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            profileId: selectedProfileId,
            frames: toDecodeFrames(batch)
          })
        });
        const payload = await response.json() as { ok?: boolean; decodedFrames?: DecodeFrameResult[]; error?: string };
        if (!response.ok || !payload.ok) throw new Error(payload.error || 'Decode request failed.');
        const rows = (payload.decodedFrames ?? []).flatMap((frame, frameIndex) => (
          frame.decodedSignals.map((signal, signalIndex) => ({
            key: `${batchStart}-${frameIndex}-${signal.signalName}-${signalIndex}`,
            timestampMs: frame.timestampMs,
            canIdHex: frame.canIdHex,
            messageName: frame.messageName,
            signal
          }))
        ));
        if (rows.length > 0) setDecodeRows((current) => [...rows, ...current].slice(0, RENDER_LIMIT));
      } catch (decodeFailure) {
        decodeCursorRef.current = batchStart;
        setDecodeError(decodeFailure instanceof Error ? decodeFailure.message : 'Decode request failed.');
      }
    }

    void decodeNextBatch();
    decodeTimerRef.current = window.setInterval(() => void decodeNextBatch(), DECODE_INTERVAL_MS);
    return () => {
      if (decodeTimerRef.current !== null) {
        window.clearInterval(decodeTimerRef.current);
        decodeTimerRef.current = null;
      }
    };
  }, [decodeEnabled, selectedProfileId]);

  async function connect(event?: FormEvent) {
    event?.preventDefault();
    track('smartcan_live_connect_clicked', {
      has_device_url: Boolean(baseUrl),
      mixed_content_likely: mixedContentLikely,
      invalid_ip: Boolean(invalidIpMessage)
    });
    stopPolling();
    setError('');
    setWarning('');
    setNotice('');
    setStatus(null);
    setLastStatusFetchError('');
    setLastFrameFetchError('');
    setFrameStreamState('idle');
    setSourceMode('device');

    if (!baseUrl) {
      setError('Enter a SmartCAN device IP or URL.');
      setConnectionState('unreachable');
      return;
    }

    if (invalidIpMessage) {
      setError(invalidIpMessage);
      setConnectionState('unreachable');
      return;
    }

    setConnectionState('connecting');
    try {
      const nextStatus = await fetchDeviceStatus(baseUrl);
      setStatus(nextStatus);
      setLastStatusFetchTime(new Date().toLocaleTimeString());
      setLastStatusFetchError('');
      setConnectionState('connected');
      setNotice('Device connected. Start live capture when ready.');
      track('smartcan_live_status_success', {
        mixed_content_likely: mixedContentLikely,
        frame_count: asNumber(readStatus(nextStatus, ['frame_count', 'frames'])) ?? undefined,
        can_error_count: asNumber(readStatus(nextStatus, ['can_error_count', 'error_count'])) ?? undefined,
        rx_overflow: asNumber(getRxOverflow(nextStatus)) ?? undefined
      });
    } catch (connectFailure) {
      setConnectionState('unreachable');
      setNotice('');
      setLastStatusFetchTime(new Date().toLocaleTimeString());
      setLastStatusFetchError(connectFailure instanceof Error ? connectFailure.message : 'Status request failed.');
      setError(explainDeviceError(connectFailure, mixedContentLikely));
    }
  }

  async function pollOnce() {
    if (!baseUrl) return;
    setFrameStreamState('checking');
    setActiveFrameEndpointUrl((current) => current || attemptedFrameUrl);
    try {
      const current = framesRef.current;
      const result = await fetchLiveFramesDetailed(baseUrl, current.length, current.at(-1));
      setActiveFrameEndpointUrl(result.endpointUrl);
      setLastFrameHttpStatus(result.httpStatus);
      setLastFrameFetchTime(new Date().toLocaleTimeString());
      setLastRawFrameCount(result.rawFrameCount);
      setLastParsedFrameCount(result.parsedFrameCount);
      setLastInvalidFrameCount(result.invalidFrameCount);
      setLastFrameFetchError('');
      appendFrames(result.frames);
      setFrameStreamState(result.frames.length > 0 ? 'receiving' : 'empty');
      setConnectionState('connected');
      if (result.frames.length > 0) setNotice('Live frames received. Decode or export when ready.');
      setError('');
    } catch (pollFailure) {
      const message = explainDeviceError(pollFailure, mixedContentLikely);
      setLastFrameFetchTime(new Date().toLocaleTimeString());
      setLastFrameFetchError(pollFailure instanceof Error ? pollFailure.message : message);
      setFrameStreamState(mixedContentLikely ? 'blocked' : 'unavailable');
      if (!status) setConnectionState('unreachable');
      setError(status ? '' : message);
      setWarning(status
        ? `Status API is reachable, but frame polling failed at ${activeFrameEndpointUrl || attemptedFrameUrl}: ${message}`
        : message);
      track('smartcan_live_frame_fetch_failed', {
        mixed_content_likely: mixedContentLikely,
        frame_endpoint_path: safeEndpointPath(activeFrameEndpointUrl || attemptedFrameUrl),
        http_status: lastFrameHttpStatus,
        frame_count: framesRef.current.length
      });
      stopPolling();
      setLiveState('paused');
    }
  }

  function startLive() {
    if (!baseUrl) {
      setNotice('');
      setError('Enter a SmartCAN device IP or URL before starting live mode.');
      setConnectionState('unreachable');
      return;
    }
    if (invalidIpMessage) {
      setNotice('');
      setError(invalidIpMessage);
      setConnectionState('unreachable');
      return;
    }
    setSourceMode('device');
    setError('');
    setWarning('');
    setNotice('Live capture started. Waiting for CAN frames from the device.');
    setFrameStreamState('checking');
    setLastFrameFetchError('');
    setActiveFrameEndpointUrl(attemptedFrameUrl);
    if (startedAt === null) setStartedAt(performance.now());
    setLiveState('live');
    stopPolling();
    void pollOnce();
    pollTimerRef.current = window.setInterval(() => void pollOnce(), POLL_MS);
  }

  function startDemo() {
    track('smartcan_demo_mode_started', {
      frame_count: framesRef.current.length,
      unique_can_ids: new Set(framesRef.current.map((frame) => frame.canId)).size
    });
    setSourceMode('demo');
    setConnectionState('connected');
    setFrameStreamState('receiving');
    setError('');
    setWarning('');
    setNotice('Demo mode started. Simulated CAN frames are now available.');
    if (startedAt === null) setStartedAt(performance.now());
    setLiveState('live');
    stopPolling();
    appendFrames(makeDemoFrames(framesRef.current.length));
    pollTimerRef.current = window.setInterval(() => {
      appendFrames(makeDemoFrames(framesRef.current.length));
    }, POLL_MS);
  }

  function pauseOrResume() {
    if (liveState === 'live') {
      stopPolling();
      setLiveState('paused');
      return;
    }
    if (sourceMode === 'demo') startDemo();
    else startLive();
  }

  function stopAndClear() {
    stopPolling();
    setLiveState('stopped');
    if (sourceMode === 'demo') setConnectionState('idle');
    setSourceMode('device');
    setFrames([]);
    framesRef.current = [];
    setDecodeRows([]);
    decodeCursorRef.current = 0;
    setSignalHints([]);
    setSignalHintState('idle');
    setSignalHintError('');
    setSelectedGraphCanId(null);
    setStartedAt(null);
    setWarning('');
    setNotice('');
    setFrameStreamState('idle');
    setLastFrameFetchError('');
    setLastRawFrameCount(0);
    setLastParsedFrameCount(0);
    setLastInvalidFrameCount(0);
  }

  function exportCsv() {
    downloadText('smartcan-live-capture.csv', exportFramesCsv(frames), 'text/csv;charset=utf-8');
  }

  function exportJson() {
    downloadText('smartcan-live-capture.json', exportFramesJson(frames), 'application/json;charset=utf-8');
  }

  const stats = useMemo(() => buildLiveStats(frames, startedAt), [frames, startedAt]);
  const visibleFrames = useMemo(() => filterFrames(frames, idFilter, dataFilter).slice(-RENDER_LIMIT).reverse(), [dataFilter, frames, idFilter]);
  const contributionCanId = visibleFrames[0]?.canIdHex ?? frames.at(-1)?.canIdHex ?? '';
  const deviceHealth = useMemo(() => getDeviceHealth(status), [status]);
  const deviceSeverity = deviceHealth.severity;
  const statusCards = [
    ['Connection', connectionState],
    ['Frame stream', frameStreamState],
    ['Device', status?.device_name ?? status?.name ?? status?.device ?? (sourceMode === 'demo' ? 'Demo Simulation Mode' : undefined)],
    ['Firmware', status?.firmware ?? status?.fw_version ?? status?.version],
    ['Device FPS', status?.fps ?? status?.frame_rate],
    ['Uptime', status?.uptime ?? status?.uptime_ms],
    ['Mode', sourceMode === 'demo' ? 'Demo Simulation Mode' : 'Wi-Fi device']
  ];
  const detailedStatusCards: DeviceStatusCard[] = [
    { label: 'Device name', value: readStatus(status, ['device_name', 'name', 'device']) ?? (sourceMode === 'demo' ? 'Demo Simulation Mode' : undefined) },
    { label: 'IP address', value: readStatus(status, ['ip', 'ip_address', 'local_ip', 'wifi_ip']) ?? (baseUrl ? baseUrl.replace(/^https?:\/\//i, '') : undefined) },
    { label: 'Logging active', value: readStatus(status, ['logging_active', 'logging']), kind: 'boolean' },
    { label: 'Current file', value: readStatus(status, ['current_file', 'file', 'filename']) },
    { label: 'Frame count', value: readStatus(status, ['frame_count', 'frames']), kind: 'integer' },
    { label: 'Ring drops', value: readStatus(status, ['ring_drops', 'drops']), kind: 'integer' },
    { label: 'CAN controller alive', value: readStatus(status, ['can_controller_alive', 'controller_alive']), kind: 'boolean' },
    { label: 'CAN bus off', value: readStatus(status, ['can_bus_off', 'bus_off']), kind: 'boolean' },
    { label: 'Error passive', value: readStatus(status, ['error_passive', 'can_error_passive']), kind: 'boolean' },
    { label: 'RX overflow', value: getRxOverflow(status), kind: 'integer', warningWhenPositive: true },
    { label: 'Battery percent', value: readStatus(status, ['battery_percent', 'battery_pct']) },
    { label: 'Battery voltage', value: readStatus(status, ['battery_mv', 'battery_millivolts']), kind: 'millivolts' },
    { label: 'Charging state', value: readStatus(status, ['charging_state', 'charging', 'power_state']) },
    { label: 'Storage used', value: getStorageUsed(status), kind: 'bytes' },
    { label: 'Storage free', value: getStorageFree(status), kind: 'bytes' },
    { label: 'Displayed time', value: displayedTime.toLocaleString() }
  ];

  return (
    <main className="live-page">
      <section className="section live-hero">
        <div>
          <p className="eyebrow">Wi-Fi Live Mode</p>
          <h1>SmartCAN Web Live</h1>
          <p className="lead">Connect a SmartCAN device to start streaming CAN frames, or try Demo Simulation Mode.</p>
          <p>Connect, start live capture, decode selected batches, then export the captured buffer when you are done.</p>
          <p>Frames stay in this browser unless Decode Live Frames is enabled, which sends small batches to the existing decode API.</p>
        </div>
        <form className="connect-card" onSubmit={connect}>
          <label>
            Device IP or URL
            <input
              value={deviceInput}
              onChange={(event) => setDeviceInput(event.target.value)}
              placeholder="192.168.4.1, 192.168.40.4, 192.168.1.120, or http://192.168.1.120"
              aria-describedby="live-device-input-help"
            />
          </label>
          <p className="input-helper" id="live-device-input-help">Select a quick IP or enter your SmartCAN device IP manually.</p>
          {invalidIpMessage && <p className="error-text">{invalidIpMessage}</p>}
          <div className="quick-hosts">
            {['192.168.4.1', '192.168.40.4', '192.168.1.120'].map((example) => (
              <button key={example} type="button" onClick={() => setDeviceInput(example)} aria-label={`Use quick IP ${example}`}>{example}</button>
            ))}
          </div>
          <div className="control-row">
            <button className="button primary live-primary-cta" disabled={connectionState === 'connecting'} type="submit" aria-label="Connect to SmartCAN device">{connectionState === 'connecting' ? 'Connecting...' : 'Connect'}</button>
            <button className="button secondary" type="button" onClick={startDemo} aria-label="Start Demo Simulation Mode">Demo Simulation Mode</button>
            <a className={`button ghost ${!deviceLocalUiUrl ? 'disabled-link' : ''}`} href={deviceLocalUiUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!deviceLocalUiUrl}>Open Device Local UI</a>
            <a className={`button secondary ${!attemptedStatusUrl ? 'disabled-link' : ''}`} href={attemptedStatusUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!attemptedStatusUrl}>Open Device Status</a>
          </div>
          <div className={`connection-state ${connectionState === 'unreachable' ? 'error' : connectionState}`}>State: {connectionState}</div>
          <p className="live-guidance-text">{connectionGuidance(connectionState, frameStreamState, sourceMode)}</p>
          <div className="live-connection-split">
            <span>Status API: <strong>{statusApiState}</strong></span>
            <span>Frame API: <strong>{frameStreamCopy(frameStreamState)}</strong></span>
            <span>Browser security: <strong>{mixedContentLikely ? 'mixed-content risk' : 'allowed / unknown'}</strong></span>
            <span>Frames received: <strong>{frames.length.toLocaleString()}</strong></span>
          </div>
          <p className="panel-note">Direct browser access requires the device API to allow CORS and local network requests.</p>
          {mixedContentLikely && (
            <div className={`diagnostic-panel mixed-content-panel ${connectionState === 'connected' ? 'soft-note' : ''}`}>
              <p className="eyebrow">Browser limitation</p>
              <h3>{connectionState === 'connected' ? 'Compatibility note' : 'HTTPS page to HTTP device'}</h3>
              <p>
                {connectionState === 'connected'
                  ? 'This browser is currently allowing the local HTTP device connection. If it stops working, use the device local UI or VS Code Extension.'
                  : 'Your browser may block HTTPS SmartCAN Web from connecting to an HTTP local device.'}
              </p>
              <ul>
                <li>Open Device Local UI: <code>{deviceLocalUiUrl}</code></li>
                <li>Open Device Status: <code>{attemptedStatusUrl}</code></li>
                <li>Use the VS Code Extension for USB live mode.</li>
                <li>Use Demo Simulation Mode if no hardware is available.</li>
              </ul>
            </div>
          )}
          {notice && <p className="success-text">{notice}</p>}
          {error && <p className="error-text">{error}</p>}
          {connectionState === 'unreachable' && attemptedStatusUrl && (
            <div className="diagnostic-panel">
              <p className="eyebrow">Connection diagnostics</p>
              <h3>Browser access check</h3>
              <ul>
                <li>Attempted URL: <code>{attemptedStatusUrl}</code></li>
                <li>Open the status link and confirm the JSON loads directly.</li>
                <li>If the direct link opens but SmartCAN Web fails, the device is online and the browser likely blocked this hosted page because of mixed-content protection, local-network access rules, or CORS policy.</li>
                <li>SmartCAN firmware must allow browser CORS access for this hosted web app.</li>
              </ul>
            </div>
          )}
        </form>
      </section>

      <section className="section live-controls-section">
        <div className="live-controls">
          <button className="button primary" type="button" disabled={!baseUrl || liveState === 'live'} onClick={startLive} aria-label="Start live CAN frame capture">Start Live</button>
          <button className="button secondary" type="button" onClick={pauseOrResume} disabled={liveState === 'stopped'} aria-label={liveState === 'live' ? 'Pause live capture' : 'Resume live capture'}>{liveState === 'live' ? 'Pause' : 'Resume'}</button>
          <button className="button ghost" type="button" onClick={stopAndClear} aria-label="Stop and clear live capture">Stop / Clear</button>
          <button className="button secondary" type="button" onClick={startDemo} aria-label="Start Demo Simulation Mode">Demo Simulation Mode</button>
          <button className="button secondary" type="button" disabled={frames.length === 0} onClick={exportCsv} aria-label="Export captured live frames as CSV">Export CSV</button>
          <button className="button secondary" type="button" disabled={frames.length === 0} onClick={exportJson} aria-label="Export captured live frames as JSON">Export JSON</button>
        </div>
        {warning && <p className="warning-text">{warning}</p>}
      </section>

      <section className="section live-diagnostics-section">
        <article className="data-panel wide live-diagnostics-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Live diagnostics</p>
              <h2>Status and frame polling</h2>
              <p className="panel-note">Status reachability and frame stream reachability are tracked separately.</p>
            </div>
            <span className={`parse-state ${frameStreamState === 'receiving' ? 'success' : frameStreamState === 'blocked' || frameStreamState === 'unavailable' || frameStreamState === 'error' ? 'error' : 'idle'}`}>
              Frames: {frameStreamCopy(frameStreamState)}
            </span>
          </div>
          <div className="diagnostics-grid">
            <div><small>Status endpoint URL</small><code>{attemptedStatusUrl || 'n/a'}</code></div>
            <div><small>Primary frame endpoint URL</small><code>{attemptedFrameUrl || 'n/a'}</code></div>
            <div><small>Frame endpoint candidates</small><code>{attemptedFrameUrls.length > 0 ? attemptedFrameUrls.join(', ') : 'n/a'}</code></div>
            <div><small>Active frame endpoint</small><code>{activeFrameEndpointUrl || 'n/a'}</code></div>
            <div><small>Last status fetch</small><strong>{lastStatusFetchTime ?? 'n/a'}</strong></div>
            <div><small>Last frame fetch</small><strong>{lastFrameFetchTime ?? 'n/a'}</strong></div>
            <div><small>Last frame HTTP status</small><strong>{lastFrameHttpStatus ?? 'n/a'}</strong></div>
            <div><small>Received / parsed / invalid</small><strong>{lastRawFrameCount.toLocaleString()} / {lastParsedFrameCount.toLocaleString()} / {lastInvalidFrameCount.toLocaleString()}</strong></div>
          </div>
          {lastStatusFetchError && <p className="warning-text">Last status fetch error: {lastStatusFetchError}</p>}
          {lastFrameFetchError && <p className="warning-text">Last frame fetch error: {lastFrameFetchError}</p>}
          {status && frameStreamState !== 'receiving' && frameStreamState !== 'idle' && (
            <p className="warning-text">Device status is reachable, but live CAN frames are not flowing through the browser. Check the frame endpoint, CORS headers, and HTTPS-to-HTTP browser blocking.</p>
          )}
        </article>
      </section>

      <section className="section">
        <div className="status-grid">
          <div className="status-card metric-emphasis"><small>Estimated FPS</small><strong>{stats.estimatedFps.toFixed(2)}</strong></div>
          <div className="status-card metric-emphasis"><small>Total frames</small><strong>{stats.totalFrames.toLocaleString()}</strong></div>
          <div className="status-card metric-emphasis"><small>Signal hints</small><strong>{Object.keys(signalHintsByCanId).length.toLocaleString()}</strong></div>
          <div className="status-card"><small>Unique CAN IDs</small><strong>{stats.uniqueCanIds.toLocaleString()}</strong></div>
          <div className="status-card"><small>Capture duration</small><strong>{formatDuration(stats.captureDurationMs)}</strong></div>
        </div>
        <div className="status-grid live-device-grid">
          {statusCards.map(([label, value]) => (
            <div className="status-card" key={label as string}>
              <small>{label as string}</small>
              <strong>{formatDeviceValue(value)}</strong>
            </div>
          ))}
        </div>
        <article className={`data-panel wide device-status-panel severity-${deviceSeverity}`}>
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Device status</p>
              <h2>{status ? `Health: ${severityCopy(deviceSeverity)}` : 'No device telemetry yet'}</h2>
              <p className="panel-note">{status ? 'Mapped from the SmartCAN device /api/status response. Missing fields are shown as unavailable.' : 'Connect a SmartCAN device or start Demo Simulation Mode.'}</p>
              {deviceHealth.reasons.length > 0 && (
                <div className="health-reasons">
                  <strong>Why?</strong>
                  <ul>
                    {deviceHealth.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                  </ul>
                </div>
              )}
              {(deviceSeverity === 'warning' || deviceSeverity === 'critical') && (
                <p className="warning-text">Device is reachable, but CAN controller reports errors. Check wiring, bitrate, termination, and bus load.</p>
              )}
            </div>
            <span className={`health-badge severity-${deviceSeverity}`}>{severityCopy(deviceSeverity)}</span>
          </div>
          {status ? (
            <div className="status-grid device-detail-grid">
              {detailedStatusCards.map((card) => (
                <div className={statusCardClass(card)} key={card.label}>
                  <small>{card.label}</small>
                  {renderStatusValue(card)}
                </div>
              ))}
            </div>
          ) : (
            <div className="live-empty-guidance">No device telemetry yet. Connect a SmartCAN device or start Demo Simulation Mode.</div>
          )}
        </article>
      </section>

      <section className="section advanced-live-tools-section" aria-labelledby="advanced-live-tools-heading">
        <div className="section-heading compact">
          <p className="eyebrow">Advanced decode tools</p>
          <h2 id="advanced-live-tools-heading">Advanced decode tools</h2>
          <p>Decode safe batches, inspect catalog hints, and keep raw live capture unchanged.</p>
        </div>
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Decode integration</p>
              <h2>Decode live frames in safe batches</h2>
              <p className="panel-note">Batches are sent every {(DECODE_INTERVAL_MS / 1000).toFixed(1)} seconds, up to {DECODE_BATCH_SIZE.toLocaleString()} new frames per request.</p>
            </div>
          </div>
          <div className="decode-controls">
            <label>
              Decode profile
              <select value={selectedProfileId} onChange={(event) => setSelectedProfileId(event.target.value)}>
                {decodeProfiles.length === 0 ? <option value="generic-obd-ii">Generic OBD-II Responses</option> : decodeProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>{profile.displayName} - {profile.confidenceSummary.label}</option>
                ))}
              </select>
            </label>
            <label className="toggle-label">
              <input type="checkbox" checked={decodeEnabled} onChange={(event) => setDecodeEnabled(event.target.checked)} />
              Decode live frames
            </label>
            <button className="button secondary" type="button" onClick={() => setContributionIntent('suggest_decode')}>Suggest decode</button>
            <button className="button ghost" type="button" onClick={() => setContributionIntent('upload_evidence')}>Upload evidence</button>
            <button className="button ghost" type="button" onClick={() => setContributionIntent('request_help')}>Request decode help</button>
          </div>
          <p className="panel-note">This is an early contribution system. Signals are reviewed before being added.</p>
          {decodeError && <p className="error-text">{decodeError}</p>}
          <div className="table-wrap decode-table-wrap">
            <table className="can-table live">
              <thead><tr><th>Timestamp</th><th>CAN ID</th><th>Message</th><th>Signal</th><th>Value</th><th>Source</th><th>Confidence</th></tr></thead>
              <tbody>
                {decodeRows.length === 0 ? (
                  <tr><td colSpan={7}>{decodeEnabled ? 'Waiting for matching decoded signals.' : 'Enable Decode live frames to populate this table.'}</td></tr>
                ) : decodeRows.map((row) => (
                  <tr key={row.key}>
                    <td>{formatTimestamp(row.timestampMs)}</td>
                    <td>{row.canIdHex}</td>
                    <td>{row.messageName ?? 'Decoded frame'}</td>
                    <td>{row.signal.signalName}</td>
                    <td>{String(row.signal.value)} {row.signal.unit ?? ''}</td>
                    <td><span className={`source-badge ${row.signal.badge.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{row.signal.badge}</span></td>
                    <td>{row.signal.confidence.label} ({Math.round(row.signal.confidence.value * 100)}%)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Decode catalog</p>
              <h2>Signal Hints</h2>
              <p className="panel-note">SmartCAN verified records, opendbc seed records, and medium-confidence public dataset records are candidate signal hints for observed CAN IDs. They do not perform numeric decoding yet.</p>
              {sourceMode === 'demo' && <p className="panel-note demo-hint-note">Demo Simulation Mode uses synthetic CAN frames for UI testing only.</p>}
            </div>
            <span className={`parse-state ${signalHintState}`}>Hints: {signalHintState}</span>
          </div>
          {signalHintError && <p className="warning-text">{signalHintError}</p>}
          <div className="contribution-list signal-list compact-signal-list">
            {signalHintState === 'loading' ? (
              <article className="decode-contribution-card"><p>Looking up signal hints...</p></article>
            ) : visibleSignalHintGroups.length === 0 ? (
              <article className="decode-contribution-card">
                <h3>No signal hints found for this live capture yet.</h3>
                <p className="panel-note">Hints appear automatically when a catalog signal matches an observed CAN ID.</p>
              </article>
            ) : visibleSignalHintGroups.map((group) => (
              <article className="decode-contribution-card verified-signal-card" key={group.canId}>
                <div className="contribution-card-heading">
                  <div>
                    <p className="eyebrow">{group.canIdHex}</p>
                    <h3>{group.hints.length.toLocaleString()} signal hint{group.hints.length === 1 ? '' : 's'}</h3>
                  </div>
                  {group.hiddenCount > 0 && <span className="signal-hint-badge">+{group.hiddenCount} more</span>}
                </div>
                <div className="signal-hint-stack">
                  {group.hints.map((hint) => (
                    <div className="signal-hint-row" key={hint.id}>
                      <div>
                        <strong>{hint.signalName}</strong>
                        {vehicleContextLabel(hint) && <span>{vehicleContextLabel(hint)}</span>}
                      </div>
                      <span className="signal-hint-badge">{signalHintBadgeLabel(hint)}</span>
                      <span>{signalHintSourceLabel(hint)}</span>
                      <span>{hint.confidenceLabel ?? `${Math.round(hint.confidence * 100)}%`}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </article>
      </section>

      <SignalGraphPanel
        compact
        frames={frames}
        selectedCanId={selectedGraphCanId}
        onSelectCanId={setSelectedGraphCanId}
        signalHintsByCanId={signalHintsByCanId}
      />

      <section className="section analyzer-actions">
        <div className="filter-panel">
          <label>CAN ID filter<input value={idFilter} onChange={(event) => setIdFilter(event.target.value)} placeholder="0x7E8" /></label>
          <label>Data byte filter<input value={dataFilter} onChange={(event) => setDataFilter(event.target.value)} placeholder="41 0C" /></label>
          <button className="button secondary" type="button" disabled={!idFilter && !dataFilter} onClick={() => { setIdFilter(''); setDataFilter(''); }}>Clear filters</button>
        </div>
      </section>

      <section className="section analyzer-table-grid">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Live frame table</p>
              <h2>Rolling latest frames</h2>
            </div>
            <span className="table-count">Showing {visibleFrames.length.toLocaleString()} of {frames.length.toLocaleString()}</span>
          </div>
          <div className="table-wrap">
            <table className="can-table live">
              <thead><tr><th>Timestamp</th><th>CAN ID</th><th>DLC</th><th>DATA</th><th>Bus</th><th>Delta ms</th></tr></thead>
              <tbody>
                {visibleFrames.length === 0 ? (
                  <tr><td colSpan={6}>No live frames captured yet. Start live capture or use Demo Simulation Mode.</td></tr>
                ) : visibleFrames.map((frame) => (
                  <tr
                    className={selectedGraphCanId === frame.canId ? 'selected-row' : ''}
                    key={`${frame.index}-${frame.canIdHex}`}
                    onClick={() => setSelectedGraphCanId(frame.canId)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedGraphCanId(frame.canId);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>{formatTimestamp(frame.timestampMs)}</td>
                    <td>
                      <span className="can-id-cell">{frame.canIdHex}</span>
                      {signalHintsByCanId[frame.canId]?.length > 0 && <span className="known-signal-badge">Signal hint</span>}
                    </td>
                    <td>{frame.dlc}</td>
                    <td>{frame.dataHex}</td>
                    <td>{frame.bus ?? ''}</td>
                    <td>{frame.deltaMs === null ? '' : frame.deltaMs.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="panel-note">Rendered rows are capped at {RENDER_LIMIT.toLocaleString()}. Capture buffer is capped at {CAPTURE_LIMIT.toLocaleString()} frames.</p>
        </article>
      </section>
      <DecodeContributionModal
        open={contributionIntent !== null}
        intent={contributionIntent ?? 'suggest_decode'}
        defaultCanId={contributionCanId}
        sourcePage="live"
        onClose={() => setContributionIntent(null)}
      />
    </main>
  );
}
